import React, { useMemo, useState } from "react";
import { isEmpty } from "lodash";

import { pushCodeLibraryToBKC } from "utils/h2kinout/api";

import Drawer from "components/Drawer";
import MultiSelect from "components/Input/MultiSelect";
import Checkbox from "components/Input/Checkbox";
import Button from "components/Button";

import classes from "./style.module.scss";

const BKC_CODE_CATEGORIES = [
    { name: "Basement Wall", type: "BasementWall" },
    { name: "Ceiling", type: "Ceiling" },
    { name: "Flat Ceiling", type: "CeilingFlat" },
    { name: "Crawlspace & Pony Wall", type: "CrawlspaceWall" },
    { name: "Exposed Floor", type: "Floor" },
    { name: "Floor Header", type: "FloorHeader" },
    { name: "Floors Added to Slab", type: "FloorsAdded" },
    { name: "Wall", type: "Wall" },
    { name: "Window", type: "Window" },
];

const getCodesForType = (codeLib, type) => {
    const { codes = {} } = codeLib[type] || {};
    return codes;
};

const getCodeOptions = (codes = {}) =>
    Object.keys(codes)
        .sort((codeA, codeB) => {
            const labelA = (codes[codeA]?.label || codeA).toLowerCase();
            const labelB = (codes[codeB]?.label || codeB).toLowerCase();
            return labelA.localeCompare(labelB);
        })
        .map((codeRef) => ({
            value: codeRef,
            label: codes[codeRef]?.label || codes[codeRef]?.value || codeRef,
        }));

const ExportBKCFieldDrawer = ({ open, close, codeLib = {} }) => {
    const [selectedCodeRefs, setSelectedCodeRefs] = useState([]);
    const [isPushing, setIsPushing] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: null, text: "" });

    const categoryOptions = useMemo(
        () =>
            BKC_CODE_CATEGORIES.map(({ name, type }) => ({
                name,
                type,
                options: getCodeOptions(getCodesForType(codeLib, type)),
            })).filter(({ options }) => options.length > 0),
        [codeLib]
    );

    const allCodeRefs = useMemo(
        () => categoryOptions.flatMap(({ options }) => options.map(({ value }) => value)),
        [categoryOptions]
    );

    const allSelected = allCodeRefs.length > 0 && allCodeRefs.every((codeRef) => selectedCodeRefs.includes(codeRef));

    const handleCategoryChange = (typeOptions, nextValues) => {
        const categoryRefs = new Set(typeOptions.map(({ value }) => value));

        setSelectedCodeRefs((prev) => [...prev.filter((codeRef) => !categoryRefs.has(codeRef)), ...nextValues]);
        setStatusMessage({ type: null, text: "" });
    };

    const handleSelectAll = (checked) => {
        setSelectedCodeRefs(checked ? allCodeRefs : []);
        setStatusMessage({ type: null, text: "" });
    };

    const handleClose = () => {
        setSelectedCodeRefs([]);
        setStatusMessage({ type: null, text: "" });
        setIsPushing(false);
        close();
    };

    const handlePush = async () => {
        if (isEmpty(selectedCodeRefs)) {
            return;
        }

        setIsPushing(true);
        setStatusMessage({ type: null, text: "" });

        try {
            const response = await pushCodeLibraryToBKC({ codeRefs: selectedCodeRefs });

            if (response && response.success) {
                const createdCount = response.created?.length || 0;
                const skippedCount = response.skipped?.length || 0;
                const omittedCount = response.omitted?.length || 0;

                const summaryParts = [
                    `${response.received || createdCount || selectedCodeRefs.length} codes sent to BKC Field`,
                ];
                if (skippedCount > 0) {
                    summaryParts.push(`${skippedCount} skipped`);
                }
                if (omittedCount > 0) {
                    summaryParts.push(`${omittedCount} omitted`);
                }

                setStatusMessage({
                    type: "success",
                    text: summaryParts.join(". ") + ".",
                });
            } else {
                setStatusMessage({
                    type: "error",
                    text: response?.message || response?.error || "Error sending codes to BKC Field",
                });
            }
        } catch (err) {
            setStatusMessage({
                type: "error",
                text:
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.message ||
                    "Error sending codes to BKC Field",
            });
        } finally {
            setIsPushing(false);
        }
    };

    return (
        <Drawer
            open={open}
            title="Export to BKC Field"
            subtitle="Select the codes you want to send."
            close={handleClose}
        >
            <div className={classes.container}>
                <p className={classes.description}>
                    Choose codes by category to push to BKC Field. Only supported HOT2000 categories are listed.
                </p>

                {isEmpty(categoryOptions) ? (
                    <p className={classes.empty}>No supported codes were found in your code library.</p>
                ) : (
                    <>
                        <Checkbox
                            name="selectAll-bkc-codes"
                            label={`Select all (${allCodeRefs.length})`}
                            large
                            input={{
                                value: allSelected,
                                onChange: (checked) => handleSelectAll(checked),
                            }}
                        />

                        {categoryOptions.map(({ name, type, options }) => (
                            <MultiSelect
                                key={type}
                                name={`bkc-codes-${type}`}
                                selectAllName={`selectAll-${type}`}
                                idPrefix={`bkc-${type}`}
                                label={`${name} (${options.length})`}
                                options={options}
                                hasSelectAll
                                input={{
                                    value: selectedCodeRefs.filter((codeRef) =>
                                        options.some(({ value }) => value === codeRef)
                                    ),
                                    onChange: (value) => handleCategoryChange(options, value),
                                }}
                            />
                        ))}
                    </>
                )}

                {statusMessage.text && (
                    <p
                        className={classes.statusMessage}
                        style={{ color: statusMessage.type === "success" ? "#28a745" : "#dc3545" }}
                    >
                        {statusMessage.text}
                    </p>
                )}

                <div className={classes.buttonContainer}>
                    <Button
                        className={classes.pushButton}
                        onClick={handlePush}
                        disabled={isEmpty(selectedCodeRefs) || isPushing || isEmpty(categoryOptions)}
                    >
                        {isPushing ? "Sending..." : "Send to BKC Field"}
                    </Button>
                </div>
            </div>
        </Drawer>
    );
};

export default ExportBKCFieldDrawer;
